import { useState, type FormEvent } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router";
import { api, errorMessage } from "../api";
import { getPasskey, passkeysSupported, PasskeyError } from "../webauthn";
import { useSession } from "../session";
import { Callout } from "../ui/bits";
import { Lockup } from "../ui/brand";

// Off in built bundles unless asked for (the e2e build sets it); on in `npm run dev`. admin-api refuses
// the dev provider outside local and test anyway.
const DEV_LOGIN = import.meta.env.DEV || (import.meta.env.VITE_ENABLE_DEV_LOGIN as string | undefined) === "1";

export function SignIn() {
  const { token, signIn } = useSession();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [email, setEmail] = useState("owner@softlife.dev");
  // Staff sign in with a code emailed to them; a passkey is an optional extra.
  const [staffEmail, setStaffEmail] = useState("");
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const expired = params.get("expired") === "1";
  const next = params.get("next") || "/";

  if (token) return <Navigate to={next} replace />;

  const done = (t: string) => {
    signIn(t);
    navigate(next.startsWith("/") && !next.startsWith("/sign-in") ? next : "/", { replace: true });
  };

  const sendCode = async (e?: FormEvent) => {
    e?.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const r = await api.requestLoginCode(staffEmail);
      setSentTo(r.sent_to);
      setCode("");
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const verify = async (e: FormEvent) => {
    e.preventDefault();
    if (!sentTo) return;
    setBusy(true);
    setError(null);
    try {
      done((await api.verifyLoginCode(sentTo, code)).token);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const passkey = async () => {
    setBusy(true);
    setError(null);
    try {
      const { ceremony, options } = await api.passkeyLoginBegin();
      done((await api.passkeyLoginFinish(ceremony, await getPasskey(options))).token);
    } catch (err) {
      if (!(err instanceof PasskeyError && err.cancelled)) setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const submitDev = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      done((await api.loginDev(email)).token);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const errorBox = error && (
    <div role="alert">
      <Callout kind="danger">{error}</Callout>
    </div>
  );

  return (
    <div className="signin">
      <main className="signin-col">
        <div className="signin-plate">
          <Lockup label="staff" />
        </div>
        <div className="card">
          <div className="card-body">
            <div>
              <h1>Sign in</h1>
              <div className="muted" style={{ marginTop: 6 }}>Staff only. Every action is audit-logged.</div>
            </div>
            {expired && <Callout kind="warn">Your console session has ended. Sign in again.</Callout>}
            {sentTo === null ? (
              <form className="stack" onSubmit={(e) => void sendCode(e)}>
                <label className="field">
                  <span className="label">Work email</span>
                  <input
                    type="email"
                    name="staff-email"
                    autoComplete="username"
                    value={staffEmail}
                    onChange={(e) => setStaffEmail(e.target.value)}
                    required
                    autoFocus
                    data-testid="signin-code-email"
                  />
                  <span className="hint">We email you a code. No password.</span>
                </label>
                {errorBox}
                <button className="primary big" type="submit" disabled={busy || !staffEmail} data-testid="signin-code-send">
                  {busy && <span className="spinner" />}
                  Email me a code
                </button>
              </form>
            ) : (
              <form className="stack" onSubmit={(e) => void verify(e)}>
                <label className="field">
                  <span className="label">Code from your email</span>
                  <input
                    className="code-input"
                    name="one-time-code"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    pattern="[0-9 ]*"
                    maxLength={9}
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    required
                    autoFocus
                    data-testid="signin-code"
                  />
                  <span className="hint">
                    If <strong>{sentTo}</strong> is a staff account, a code is on its way. It works once, for 10 minutes.
                  </span>
                </label>
                {errorBox}
                <button className="primary big" type="submit" disabled={busy || code.replace(/\s/g, "").length < 8} data-testid="signin-code-verify">
                  {busy && <span className="spinner" />}
                  Sign in
                </button>
                <div className="signin-links">
                  <button type="button" className="link" onClick={() => void sendCode()} disabled={busy} data-testid="signin-code-resend">Send a new code</button>
                  <button type="button" className="link" onClick={() => { setSentTo(null); setCode(""); setError(null); }} disabled={busy}>Use a different email</button>
                </div>
              </form>
            )}
            {passkeysSupported() && sentTo === null && (
              <>
                <div className="divider">or</div>
                <button type="button" onClick={() => void passkey()} disabled={busy} data-testid="signin-passkey">
                  Sign in with a passkey
                </button>
                <div className="muted" style={{ fontSize: 13 }}>If you've added one on this device. You can add or remove passkeys any time under Security.</div>
              </>
            )}
            <div className="muted" style={{ fontSize: 13 }}>
              New here? Open the invite email you were sent and follow the link to claim your account.
            </div>
            {DEV_LOGIN && (
              <>
                <div className="divider">or use a dev account</div>
                <form className="stack" onSubmit={submitDev}>
                  <label className="field">
                    <span className="label">Staff email</span>
                    <input
                      type="email"
                      name="email"
                      autoComplete="username"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      data-testid="signin-email"
                    />
                    <span className="hint">
                      Local and test environments only. Signs in as <code>dev:{email || "<email>"}</code>.
                    </span>
                  </label>
                  <button className="primary big" type="submit" disabled={busy || !email} data-testid="signin-submit">
                    {busy && <span className="spinner" />}
                    Sign in
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
        <p className="signin-foot">The operator console: players, payments, live-ops and safety for every SoftLife market.</p>
      </main>
    </div>
  );
}
